/* eslint-disable camelcase */
import { trackEvent, getFormName } from '@common/utils'
import {
    BillingPeriod,
    BillingPeriodTimePeriodMapper,
    CURRENCY,
    PRODUCT_FAMILY_KEY,
    TimePeriod,
    TimePeriodBillingPeriodMapper,
    TRACKING_EVENTS,
    VIEW_PATH,
} from '@common/constants'
import { BuyCartData, BuyPurchaseData, BuyCustomizeItemsData } from '@buy/state/tracking/state'
import { PageViewContentData, SessionInfo, TrackECommerceData } from '@common/state/tracking/state'
import { BUY_TYPE, BUY_USER_TYPES, BUY_TRACKING_COMPONENTS } from '@buy/constants'
import { cloneDeep, isEmpty } from 'lodash-es'
import { OrderSummaryLine } from '@buy/state/order-summary/order-summary'
import { calculateVolumeDiscount, getPrice, getPriceTier } from './volume-components-helpers'
import { ValidCoupon } from './coupon'
import { getIsOnePayFlow } from './get-is-onepay-flow'
import { getUsagePriceType, isNoneUsagePrice, isPayGoUsagePrice } from './get-usage-price-values'

const contentType: Record<string, string> = {
    [VIEW_PATH.PATH_CUSTOMIZE]: 'plan',
    [VIEW_PATH.PATH_CHECKOUT]: 'buy-form',
}

const enum CartAction {
    ADD = 'add',
    REMOVE = 'remove',
    NONE = 'none',
}

export const getBuyType = (userType: string) => {
    if (userType === BUY_USER_TYPES.TRIALER) {
        return BUY_TYPE.TRIAL_CONVERSION
    }
    if (userType === BUY_USER_TYPES.ADDON) {
        return BUY_TYPE.ADDON
    }
    return BUY_TYPE.DIRECT_BUY
}

interface PreviousTrackData {
    step: number
    cartData: BuyCartData | null
}
const previousTrackData: PreviousTrackData = {
    step: 0, // default value
    cartData: null,
}
const setPreviousStep = (step: number) => {
    previousTrackData.step = step
}
const setPreviousCart = (cartData: BuyCartData) => {
    previousTrackData.cartData = cloneDeep(cartData)
}

interface SkuCartData {
    currency: CURRENCY
    line: OrderSummaryLine
    billingFrequency: TimePeriod
    billingPeriod: BillingPeriod
    billingPeriodKeys: string[]
    isAddon: boolean
    productFamilyName: string
    couponCode: string
    validCoupons: ValidCoupon[]
    mainCartLine: OrderSummaryLine | undefined
}

const processSkuData = (data: SkuCartData) => {
    const {
        currency,
        line,
        billingFrequency,
        billingPeriod,
        billingPeriodKeys,
        isAddon,
        productFamilyName,
        mainCartLine,
    } = data
    const actualQuantity = !isEmpty(mainCartLine) ? mainCartLine.quantity : line.quantity
    const actualMultiplier = !isEmpty(mainCartLine) ? mainCartLine.multiplier : line.multiplier
    const unitVolumeDiscount = calculateVolumeDiscount(
        line,
        !isAddon ? actualMultiplier : actualQuantity,
        billingPeriodKeys,
        billingPeriod === BillingPeriod.ANNUAL
    )
    const priceTier = getPriceTier(!isAddon ? actualMultiplier : actualQuantity, line.allPrices[billingPeriod])
    const couponDiscount = data.validCoupons.find((item) => item.sku === line.key)?.discount ?? 0
    const discount = (unitVolumeDiscount + couponDiscount) / (!isAddon ? line.multiplier : line.quantity)

    const usagePriceType = getUsagePriceType(line.allPrices, line.usagePrice)
    const calculatedBillingFrequency = isNoneUsagePrice(usagePriceType) ? billingFrequency : TimePeriod.Month
    const calculatedBillingPeriod = isNoneUsagePrice(usagePriceType) ? billingPeriod : BillingPeriod.MONTHLY
    const skuPrice = getPrice(1, line.allPrices[calculatedBillingPeriod])
    const calculatedPrice = skuPrice * (calculatedBillingPeriod === BillingPeriod.ANNUAL ? 12 : 1)

    return {
        currency,
        item_brand: productFamilyName,
        item_category: productFamilyName,
        item_category2: line.skuName,
        item_category3: calculatedBillingFrequency,
        item_category4: '1',
        item_category5: undefined,
        item_name: `${isAddon ? `${productFamilyName} ` : ''}${
            line.skuName
        }_${calculatedBillingFrequency.toLowerCase()}`,
        item_id: `${line.key}_${calculatedBillingFrequency.toLowerCase()}`,
        item_variant: isAddon ? 'Add-on' : 'Product',
        price: isPayGoUsagePrice(usagePriceType) ? 0 : calculatedPrice,
        quantity: !isAddon ? line.multiplier : line.quantity,
        discount,
        product_discount_tier: priceTier === 0 ? undefined : `Tier${priceTier}`,
        coupon: data.couponCode,
    }
}

const processCartData = (productData: BuyCartData, mainCartData?: BuyCartData) => {
    const { baseLine, addonLines, additionalLines, billingPeriod, currency, billingPeriodKeys } = productData

    let planInfo: TrackECommerceData | null = null
    let cartItems: TrackECommerceData[] = []
    const billingFrequency = BillingPeriodTimePeriodMapper[billingPeriod]

    if (!isEmpty(baseLine)) {
        planInfo = processSkuData({
            currency,
            line: baseLine,
            billingFrequency,
            billingPeriod,
            billingPeriodKeys,
            isAddon: false,
            productFamilyName: productData.productFamilyName,
            couponCode: productData.couponCode,
            validCoupons: productData.validCoupons,
            mainCartLine: mainCartData?.baseLine,
        })
        cartItems = [...cartItems, planInfo]
    }

    let additionalLinesInfoList: TrackECommerceData[] | null = null
    if (!isEmpty(additionalLines)) {
        additionalLinesInfoList = additionalLines.map((line) => {
            const addonInfo: TrackECommerceData = processSkuData({
                currency,
                line,
                billingFrequency,
                billingPeriod,
                billingPeriodKeys,
                isAddon: false,
                productFamilyName: productData.productFamilyName,
                couponCode: productData.couponCode,
                validCoupons: productData.validCoupons,
                mainCartLine: mainCartData?.additionalLines.find((item) => line.key === item.key),
            })
            return addonInfo
        })
        cartItems = [...cartItems, ...additionalLinesInfoList]
    }

    let addonInfoList: TrackECommerceData[] | null = null
    if (!isEmpty(addonLines)) {
        addonInfoList = addonLines.map((line) => {
            const addonInfo: TrackECommerceData = processSkuData({
                currency,
                line,
                billingFrequency,
                billingPeriod,
                billingPeriodKeys,
                isAddon: true,
                productFamilyName: productData.productFamilyName,
                couponCode: productData.couponCode,
                validCoupons: productData.validCoupons,
                mainCartLine: mainCartData?.addonLines.find((item) => line.key === item.key),
            })
            return addonInfo
        })
        cartItems = [...cartItems, ...addonInfoList]
    }
    return cartItems
}

export const trackAddToCart = (productData: BuyCartData, platformProduct: string, mainCartData?: BuyCartData) => {
    const { tier, totalDiscount } = productData
    const cartItems = processCartData(productData, mainCartData)
    const eventData = {
        ecommerce: {
            items: cartItems,
        },
        actionField: {
            coupon: productData.couponCode || undefined,
        },
        discount_tier: tier,
        discount_total: totalDiscount,
        platform_product: platformProduct,
    }
    trackEvent({ event: TRACKING_EVENTS.ADD_TO_CART, eventData })
}

export const trackRemoveFromCart = (productData: BuyCartData, mainCartData?: BuyCartData) => {
    const { tier, totalDiscount } = productData
    const cartItems = processCartData(productData, mainCartData)
    const eventData = {
        ecommerce: {
            items: cartItems,
        },
        actionField: {
            coupon: productData.couponCode || undefined,
        },
        discount_tier: tier,
        discount_total: totalDiscount,
    }
    trackEvent({ event: TRACKING_EVENTS.REMOVE_FROM_CART, eventData })
}

const compareOrderLines = (newLine: OrderSummaryLine, oldLine: OrderSummaryLine) => {
    if (newLine.quantity > oldLine.quantity) {
        const resultLine: OrderSummaryLine = {
            ...newLine,
            quantity: newLine.quantity - oldLine.quantity,
            multiplier: newLine.multiplier - oldLine.multiplier,
        }
        return { resultLine, action: CartAction.ADD }
    }
    if (newLine.quantity < oldLine.quantity) {
        const resultLine: OrderSummaryLine = {
            ...newLine,
            quantity: oldLine.quantity - newLine.quantity,
            multiplier: oldLine.multiplier - newLine.multiplier,
        }
        return { resultLine, action: CartAction.REMOVE }
    }
    return { resultLine: newLine, action: CartAction.NONE }
}

const getLinesByAction = (previousLines: OrderSummaryLine[], newLines: OrderSummaryLine[]) => {
    // Retrieve the skus in old and new cart data
    const oldDataSkus = previousLines.map((item) => item.skuName)
    const newDataSkus = newLines.map((item) => item.skuName)

    // Check for skus that were newly added or completely removed
    const skusToRemove = oldDataSkus.filter((item) => !newDataSkus.includes(item))
    const skusToAdd = newDataSkus.filter((item) => !oldDataSkus.includes(item))
    // These skus are present in both the new cart data and old data , so we need to check for the difference in quantity
    const skusNeededToBeCompared = oldDataSkus.filter((item) => newDataSkus.includes(item))

    let linesToRemove = previousLines.filter((item) => skusToRemove.includes(item.skuName))
    let linesToAdd = newLines.filter((item) => skusToAdd.includes(item.skuName))

    const comparedSkuLines = skusNeededToBeCompared.map((item2) => {
        return compareOrderLines(
            newLines.find((item) => item.skuName === item2)!,
            previousLines.find((item) => item.skuName === item2)!
        )
    })
    comparedSkuLines.forEach((item) => {
        if (item.action === CartAction.ADD) linesToAdd = [...linesToAdd, item.resultLine]
        else if (item.action === CartAction.REMOVE) linesToRemove = [...linesToRemove, item.resultLine]
    })
    return {
        removedLines: linesToRemove,
        addedLines: linesToAdd,
    }
}

export const checkCartData = (cartData: BuyCartData, platformProduct: string) => {
    const previousCartData = previousTrackData.cartData
    if (previousCartData) {
        const { plan } = cartData
        const currentCartPlan = plan
        const previousCartPlan = previousCartData.plan

        if (currentCartPlan.name !== previousCartPlan.name) {
            trackRemoveFromCart(previousCartData)
            trackAddToCart(cartData, platformProduct)
        } else {
            // Check if theres a change in the base plan quantity
            const baseLineChanges = compareOrderLines(cartData.baseLine!, previousCartData.baseLine!)
            // Check if theres a change in the additional products quantity
            const additionalLinesDifference = getLinesByAction(
                previousCartData.additionalLines,
                cartData.additionalLines
            )
            // Check if theres a change in the addons quantity
            const addonLinesDifference = getLinesByAction(previousCartData.addonLines, cartData.addonLines)

            const addedCart: BuyCartData = {
                ...cartData,
                additionalLines: additionalLinesDifference.addedLines,
                addonLines: addonLinesDifference.addedLines,
                baseLine: undefined,
            }
            const removededCart: BuyCartData = {
                ...cartData,
                additionalLines: additionalLinesDifference.removedLines,
                addonLines: addonLinesDifference.removedLines,
                baseLine: undefined,
            }
            if (baseLineChanges.action === CartAction.ADD) {
                addedCart.baseLine = baseLineChanges.resultLine
            } else if (baseLineChanges.action === CartAction.REMOVE) {
                removededCart.baseLine = baseLineChanges.resultLine
            }
            if (!isEmpty(addedCart.baseLine) || !isEmpty(addedCart.additionalLines) || !isEmpty(addedCart.addonLines))
                trackAddToCart(addedCart, platformProduct, cartData)
            if (
                !isEmpty(removededCart.baseLine) ||
                !isEmpty(removededCart.additionalLines) ||
                !isEmpty(removededCart.addonLines)
            )
                trackRemoveFromCart(removededCart, cartData)
        }
    } else {
        // just for double check, because there will always be previous cartData
        // if you are in checkCartData function because of the order in which events are fired
        trackAddToCart(cartData, platformProduct)
    }
}

export const trackCustomizeViewItem = (customizeItemsData: BuyCustomizeItemsData) => {
    const { planData, billingFrequency, currency, organizers, productFamilyName, baseLine } = customizeItemsData

    // See "view_item" column in spreadsheet attached to WAE1-174
    const skuPrice = getPrice(1, baseLine!.allPrices[TimePeriodBillingPeriodMapper[billingFrequency]])

    const billingFrequencyLowerCase = billingFrequency.toLowerCase()
    const planInfo: TrackECommerceData = {
        currency,
        item_brand: productFamilyName, // product_name
        item_category: productFamilyName, // product_name
        item_category2: planData.name, // product_id
        item_category3: billingFrequency, // product_period
        item_category4: '1', // product_duration
        item_name: `${planData.name}_${billingFrequencyLowerCase}`, // Product / add-on name with period
        item_id: `${planData.sKU}_${billingFrequencyLowerCase}`, // Product / add-on unique identifier, sku with period
        item_variant: 'Product', // Specify if it's a "Product" or an "Add-on"
        price: skuPrice * (billingFrequency === TimePeriod.Year ? 12 : 1), // product_price
        quantity: organizers, // product_quantity
    }

    const eventData = {
        ecommerce: {
            items: [planInfo],
        },
    }
    trackEvent({ event: TRACKING_EVENTS.VIEW_ITEM, eventData })
}

export const trackViewContent = (userData: PageViewContentData, platformProduct: string, pageName: string) => {
    const formName = getFormName(pageName)
    const contentTypeName = `/${pageName.split('/')[2]}`
    const eventData = {
        platform_environment: `${platformProduct}-${process.env.ENVIRONMENT}`,
        platform_product: platformProduct,
        platform_type: 'marketing',
        platform_locale: userData.locale,
        platform_country: userData.country,
        platform_language: userData.language,
        section: 'web',
        // TODO: load based on product
        sub_section: 'resolve',
        template: pageName,
        content_type: `${pageName}-${contentType[contentTypeName]}`,
        page_type: pageName,
        content_number: 1,
        login_status: userData.isUserLoggedIn,
        funnel_name: `${platformProduct}:marketing:${pageName}`,
        form_name: formName,
    }
    trackEvent({ event: TRACKING_EVENTS.VIEW_CONTENT, eventData })
}

export const trackSessionInfo = (sessionInfo: SessionInfo) => {
    const eventData = {
        session_last: sessionInfo.sessionLast,
        session_number: sessionInfo.sessionNumber,
        user_type: sessionInfo.userType,
        is_logged_in: sessionInfo.isUserLoggedIn,
    }
    trackEvent({ event: TRACKING_EVENTS.SESSION_INFORMATION, eventData })
}

export const trackBeginCheckout = (productData: BuyCartData, currentStep: number, platformProduct: string) => {
    const isOnePayFlow = getIsOnePayFlow()
    const { tier, totalDiscount } = productData
    const cartItems = processCartData(productData)

    const eventData = {
        ecommerce: {
            items: cartItems,
        },
        actionField: {
            step: currentStep,
            coupon: productData.couponCode || undefined,
        },
        discount_tier: tier,
        discount_total: totalDiscount,
        platform_product: platformProduct,
    }

    trackEvent({ event: TRACKING_EVENTS.BEGIN_CHECKOUT, eventData })

    // if user navigates directly to checkout page (step 2), check cart data
    if (previousTrackData.step === 0 && currentStep === 2) {
        checkCartData(productData, platformProduct)
    }
    // if user navigates from customize page (step 1) to checkout page (step 2), check cart data
    if (previousTrackData.step === 1 && currentStep === 2) {
        checkCartData(productData, platformProduct)
    }
    // if user starts off in customize page, do not set previouse cart data yet, as it remains undefined
    if (previousTrackData.step !== 0) {
        setPreviousCart(productData)
    }
    setPreviousStep(currentStep)

    if (currentStep === 2 && productData.isExistingUser) {
        trackBeginCheckout(productData, 3, platformProduct)
        if (!isOnePayFlow) {
            // For existing users returning to the onepay flow the "begin_checkout step 4" event gets
            // tracked upon clicking the "Proceed to Purchase" button instead of here.
            // See: src/products/buy/connectedComponents/CheckoutForm/index-onepay.tsx
            trackBeginCheckout(productData, 4, platformProduct)
        }
    }
}

export const trackPurchase = (
    productData: BuyCartData,
    purchaseData: BuyPurchaseData,
    saveToSessionStorage = false
) => {
    const { tier, totalDiscount } = productData
    const cartItems = processCartData(productData)
    const buyType = getBuyType(purchaseData.userType)
    const eventData = {
        ecommerce: {
            items: cartItems.map((item) => ({
                ...item,
                buy_type: buyType,
            })),
        },
        actionField: {
            transaction_id: purchaseData.transactionID,
            coupon: productData.couponCode || undefined,
            tax: purchaseData.calculatedTax,
            revenue: purchaseData.calculatedNetTotal,
        },
        plan: BillingPeriodTimePeriodMapper[productData.billingPeriod],
        buy_type: buyType,
        conversion_type: 'purchase',
        discount_tier: tier,
        discount_total: totalDiscount,
    }
    trackEvent({ event: TRACKING_EVENTS.PURCHASE, eventData }, saveToSessionStorage)
}

export const trackPurchaseError = (errorMessage: string, plan: string, buyType: string) => {
    const eventData = {
        plan, // 'annual' or 'monthly'
        buy_type: buyType, // 'add-on' or 'direct buy'
        conversion_type: 'purchase', // static value, case-sensitive
        error_purchase_message: errorMessage,
    }
    trackEvent({ event: TRACKING_EVENTS.ERROR_PURCHASE, eventData })
}

export const trackCompleteBuy = (
    userType: string,
    userEmail: string,
    productData: BuyCartData,
    platformProduct: string,
    saveToSessionStorage = false
) => {
    const eventData = {
        platform_product: platformProduct,
        buy_type: getBuyType(userType),
        plan: BillingPeriodTimePeriodMapper[productData.billingPeriod],
        conversion_type: 'purchase',
        user_email: userEmail,
    }
    trackEvent({ event: TRACKING_EVENTS.COMPLETE_BUY_FLOW, eventData }, saveToSessionStorage)
}

export const trackFormError = (formIds: string[], formErrors: string[], pageName: string) => {
    const eventData = {
        form_error_id: formIds.toString(),
        form_error_message: formErrors.toString(),
        form_name: getFormName(pageName),
    }
    trackEvent({ event: TRACKING_EVENTS.ERROR_FORM, eventData })
}

export const trackUserInformation = (userType: string, userEmail: string, productData: BuyCartData) => {
    const eventData = {
        buy_type: getBuyType(userType),
        plan: BillingPeriodTimePeriodMapper[productData.billingPeriod],
        user_email: userEmail,
    }
    trackEvent({ event: TRACKING_EVENTS.USER_INFORMATION, eventData })
}

export const trackEmailValidation = (formName: string, userEmail: string) => {
    const eventData = {
        form_name: formName,
        user_email: userEmail,
    }
    trackEvent({ event: TRACKING_EVENTS.EMAIL_VALIDATION, eventData })
}

export const trackLogin = (userEmail: string) => {
    const eventData = {
        platform_product: PRODUCT_FAMILY_KEY.G2R, // TODO need to fix/possibly fixed in other branch
        user_email: userEmail,
    }
    trackEvent({ event: TRACKING_EVENTS.LOGIN, eventData })
}

export const trackAbandonForm = () => {
    const eventData = {
        form_name: BUY_TRACKING_COMPONENTS.CHECKOUT_PAGE,
    }
    trackEvent({ event: TRACKING_EVENTS.ABANDON_FORM, eventData })
}
